import type { orpcClient, Output } from "@/lib/orpc";

export type Trend = Output<typeof orpcClient.crm.analytics.trend>;
export type TourAnalytics = Output<typeof orpcClient.crm.analytics.tours>;
export type TeamAnalytics = Output<typeof orpcClient.crm.analytics.team>;
export type ActivityAnalytics = Output<typeof orpcClient.crm.analytics.activity>;
export type ShelbyAnalytics = Output<typeof orpcClient.shelby.analytics>;

export const WINDOWS = [7, 30, 90, 180, 365] as const;
export type WindowDays = (typeof WINDOWS)[number];

export const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
export const moneyOrDash = (value: number | null) => (value === null ? "-" : money(value));

/** An event's spend to the cent, or a dash. */
export const dollars = (value: number | null) =>
  value === null ? "-" : `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

/** "+3 vs the 30 days before", or "same as" when nothing moved. */
export function versus(current: number, previous: number, days: number): string {
  const change = current - previous;
  const sign = change > 0 ? "+" : "";
  return change === 0
    ? `same as the ${days} days before`
    : `${sign}${change} vs the ${days} days before`;
}

/** A period's first day, short: "Sep 21" for a week, "Sep 2026" for a month. */
export function periodLabel(periodStart: string, bucket: "week" | "month"): string {
  const date = new Date(`${periodStart}T00:00:00Z`);
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    ...(bucket === "month" ? { year: "numeric" } : { day: "numeric" }),
  });
}

export function seconds(value: number | null): string {
  if (value === null) return "-";
  if (value < 60) return `${value}s`;
  return `${Math.round(value / 60)}m`;
}

const DAY_MS = 86_400_000;

/** A stored calendar day (a date string or its UTC midnight) as YYYY-MM-DD. */
export const calendarDay = (value: string | Date) => new Date(value).toISOString().slice(0, 10);

/** Today on this device's calendar, YYYY-MM-DD, the day a tour list is judged against. */
export const localToday = () => new Date().toLocaleDateString("en-CA");

/** "Today", "Tomorrow", "Thursday" within the week, "Thu, Oct 9" beyond it. */
export function dayWord(day: string, today: string): string {
  const diff = Math.round((Date.parse(day) - Date.parse(today)) / DAY_MS);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  const date = new Date(`${day}T00:00:00Z`);
  return date.toLocaleDateString(
    "en-US",
    diff > 1 && diff < 7
      ? { timeZone: "UTC", weekday: "long" }
      : { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" },
  );
}

/** Whole days from one moment to another, never below zero. */
export const daysBetween = (from: string | Date, to: string | Date = new Date()) =>
  Math.max(0, Math.floor((new Date(to).getTime() - new Date(from).getTime()) / DAY_MS));

/** Whole years since a date of birth, birthday counted. */
export function ageOf(dob: string | Date, now = new Date()) {
  const born = new Date(dob);
  const years = now.getUTCFullYear() - born.getUTCFullYear();
  const before =
    now.getUTCMonth() < born.getUTCMonth() ||
    (now.getUTCMonth() === born.getUTCMonth() && now.getUTCDate() < born.getUTCDate());
  return before ? years - 1 : years;
}
